import React, { useEffect, useRef } from 'react';
import { compileLut, compileLutPair, spatialParams, gradeSignature, activeMasks, maskScalars, lutFromStored, LUT_IMPORT_SIZE } from '../utils/grade';

const MAX_MASKS = 4;
const MAX_HEAL = 8;

const LUT_SIZE = 33;
const DEFAULT_MAX_RENDER_W = 1600;

const VERT = `#version 300 es
in vec2 aPos;
out vec2 vUv;
void main(){
  vUv = vec2(aPos.x * 0.5 + 0.5, 0.5 - aPos.y * 0.5);
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;
precision highp sampler3D;
in vec2 vUv;
out vec4 frag;
uniform sampler2D uImage;
uniform sampler3D uLut;
uniform sampler3D uLut2;
uniform vec2 uTexel;
uniform vec2 uCropOff, uCropScale;
uniform vec2 uFlip;            // (±1, ±1) horizontal/vertical flip
uniform float uRot, uImgAspect; // straighten (radians, inverse of ffmpeg rotate) + W/H
uniform float uClarity, uTexture, uSharp, uVignette, uGrain, uTime, uLutScale, uLutOffset;
uniform float uHalation, uGlow, uHasLut2, uDenoise;
#define MAXM 4
uniform int uMaskN;
uniform float uMcx[MAXM], uMcy[MAXM], uMrx[MAXM], uMry[MAXM], uMfeat[MAXM], uMinv[MAXM], uMamt[MAXM];
uniform float uMev[MAXM], uMblk[MAXM], uMk[MAXM], uMtF[MAXM], uMtnF[MAXM], uMf[MAXM];
uniform float uMtype[MAXM], uMang[MAXM], uMrEn[MAXM], uMrlo[MAXM], uMrhi[MAXM], uMrf[MAXM];
uniform float uMcEn[MAXM], uMcCos[MAXM], uMcSin[MAXM], uMcW[MAXM], uMcChroma[MAXM], uMcF[MAXM];
#define MAXH 8
uniform int uHealN;
uniform vec2 uHcen[MAXH], uHoff[MAXH];   // target center + source offset (uv)
uniform float uHr[MAXH], uHfeat[MAXH];   // radius (frac of width) + feather (0..1)

vec3 lut(vec3 c){
  c = clamp(c, 0.0, 1.0) * uLutScale + uLutOffset;
  return texture(uLut, c).rgb;
}
// Imported creative LUT (33³) applied AFTER the grade LUT — same scale/offset.
vec3 lut2(vec3 c){
  c = clamp(c, 0.0, 1.0) * uLutScale + uLutOffset;
  return texture(uLut2, c).rgb;
}
// Healing / spot removal in source space (before LUT/grade). Each spot clones
// from a feathered circle at uv+offset over the target. Mirrors the ffmpeg
// healFilters geq exactly: aspect-corrected circle, linear feather, raw-source
// offset samples, sequential blend. Identity when uHealN==0 (video path).
vec3 healedSample(vec2 uv){
  vec3 c = texture(uImage, uv).rgb;
  for(int i = 0; i < MAXH; i++){
    if(i >= uHealN) break;
    float dx = uv.x - uHcen[i].x;
    float dy = (uv.y - uHcen[i].y) / uImgAspect;
    float d = sqrt(dx*dx + dy*dy);
    float r = max(uHr[i], 1e-4);
    float feat = clamp(uHfeat[i], 0.0, 0.999);
    float denom = max(r * feat, 1e-4);
    float w = clamp((r - d) / denom, 0.0, 1.0);
    if(w > 0.0) c = mix(c, texture(uImage, uv + uHoff[i]).rgb, w);
  }
  return c;
}
vec3 gradedAt(vec2 uv){
  vec3 c = lut(healedSample(uv));
  if(uHasLut2 > 0.5) c = lut2(c);
  return c;
}
float hash(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

void main(){
  // Geometry: undo the (flip → straighten) transform to find the source sample.
  // Mirrors the ffmpeg bake (hflip/vflip → rotate → crop). Identity when uRot=0
  // and uFlip=(1,1), so the video path is unchanged.
  vec2 n = uCropOff + vUv * uCropScale;
  vec2 q = n - 0.5;
  q.x *= uImgAspect;
  float cR = cos(uRot), sR = sin(uRot);
  q = vec2(q.x * cR - q.y * sR, q.x * sR + q.y * cR);
  q.x /= uImgAspect;
  q *= uFlip;
  vec2 suv = q + 0.5;                        // sample point in source space
  if(suv.x < 0.0 || suv.x > 1.0 || suv.y < 0.0 || suv.y > 1.0){
    frag = vec4(0.0, 0.0, 0.0, 1.0);        // matches ffmpeg rotate's black fill
    return;
  }
  vec2 st = uTexel * uCropScale;            // neighbour step in source space
  vec3 base = gradedAt(suv);
  if(uDenoise > 0.0){
    // Edge-preserving (bilateral) denoise — approximates the ffmpeg hqdn3d bake.
    // 3x3 taps weighted by colour similarity so edges survive; radius + chroma
    // tolerance grow with strength. Runs before clarity/sharpen, like the bake.
    float radius = 1.0 + uDenoise * 2.0;
    float sigmaC = 0.06 + uDenoise * 0.22;
    float inv2s2 = 1.0 / (2.0 * sigmaC * sigmaC);
    vec3 sum = base; float wsum = 1.0;
    for(int dx = -1; dx <= 1; dx++){
      for(int dy = -1; dy <= 1; dy++){
        if(dx == 0 && dy == 0) continue;
        vec3 s = gradedAt(suv + vec2(float(dx), float(dy)) * st * radius);
        vec3 d = s - base;
        float wc = exp(-dot(d, d) * inv2s2);
        sum += s * wc; wsum += wc;
      }
    }
    base = mix(base, sum / wsum, clamp(uDenoise, 0.0, 1.0));
  }
  vec3 col = base;
  if(uClarity != 0.0 || uTexture != 0.0){
    float r = 6.0;
    vec3 b = gradedAt(suv + vec2(r,0.0)*st) + gradedAt(suv - vec2(r,0.0)*st)
           + gradedAt(suv + vec2(0.0,r)*st) + gradedAt(suv - vec2(0.0,r)*st);
    b *= 0.25;
    col += (uClarity * 1.5 + uTexture * 0.6) * (base - b);
  }
  if(uSharp != 0.0){
    float r = 1.5;
    vec3 b = gradedAt(suv + vec2(r,0.0)*st) + gradedAt(suv - vec2(r,0.0)*st)
           + gradedAt(suv + vec2(0.0,r)*st) + gradedAt(suv - vec2(0.0,r)*st);
    b *= 0.25;
    col += uSharp * 1.5 * (base - b);
  }
  if(uVignette > 0.0){
    vec2 d = vUv - 0.5;
    float rr = length(d) * 1.4142;
    col *= 1.0 - uVignette * smoothstep(0.4, 0.95, rr);
  }
  if(uHalation > 0.0 || uGlow > 0.0){
    // Cheap highlight bloom: average a wide ring of graded taps, keep the
    // bright part, screen it back. Halation warms the bloom (red/orange halo).
    float rr = 9.0;
    vec3 acc = base;
    acc += gradedAt(suv + vec2(rr,0.0)*st) + gradedAt(suv - vec2(rr,0.0)*st);
    acc += gradedAt(suv + vec2(0.0,rr)*st) + gradedAt(suv - vec2(0.0,rr)*st);
    float r2 = rr * 0.6;
    acc += gradedAt(suv + vec2(r2,r2)*st) + gradedAt(suv - vec2(r2,r2)*st);
    acc += gradedAt(suv + vec2(r2,-r2)*st) + gradedAt(suv + vec2(-r2,r2)*st);
    acc *= (1.0/9.0);
    float lb = dot(acc, vec3(0.2126, 0.7152, 0.0722));
    vec3 bloom = acc * smoothstep(0.6, 1.0, lb);
    vec3 warm = vec3(1.0, 0.55, 0.4);
    vec3 add = bloom * uGlow * 0.9 + bloom * warm * uHalation * 1.1;
    col = 1.0 - (1.0 - clamp(col,0.0,1.0)) * (1.0 - clamp(add,0.0,1.0));
  }
  if(uGrain > 0.0){
    float n = hash(vUv * vec2(1920.0, 1080.0) + uTime) - 0.5;
    col += n * uGrain * 0.15;
  }
  // Local radial masks (post-LUT/spatial). Same math as the ffmpeg geq export.
  col = clamp(col, 0.0, 1.0);
  for(int i = 0; i < MAXM; i++){
    if(i >= uMaskN) break;
    float a;
    if(uMtype[i] > 1.5){
      // Full-frame colour-keyed mask — the hue/chroma gate below selects pixels.
      a = 1.0;
    } else if(uMtype[i] < 0.5){
      // Radial ellipse
      float dx = (vUv.x - uMcx[i]) / max(uMrx[i], 1e-4);
      float dy = (vUv.y - uMcy[i]) / max(uMry[i], 1e-4);
      float d = sqrt(dx*dx + dy*dy);
      float feather = clamp(uMfeat[i], 0.001, 1.0);
      float inner = 1.0 - feather;
      float t = clamp((d - inner) / max(1.0 - inner, 1e-3), 0.0, 1.0);
      float sm = t*t*(3.0 - 2.0*t);
      a = (uMinv[i] > 0.5 ? sm : 1.0 - sm);
    } else {
      // Linear gradient along the mask angle
      vec2 ax = vec2(cos(uMang[i]), sin(uMang[i]));
      float s = dot(vUv - vec2(uMcx[i], uMcy[i]), ax);
      float fw = max(uMfeat[i], 1e-3);
      float t = clamp(s / (2.0*fw) + 0.5, 0.0, 1.0);
      float sm = t*t*(3.0 - 2.0*t);
      a = (uMinv[i] > 0.5 ? 1.0 - sm : sm);
    }
    a *= uMamt[i];
    if(uMrEn[i] > 0.5){
      // Luminance-range gate (color/luma keyer) on the incoming pixel.
      float Lg = dot(col, vec3(0.2126, 0.7152, 0.0722));
      float rf = max(uMrf[i], 1e-3);
      float g1 = clamp((Lg - (uMrlo[i] - rf)) / rf, 0.0, 1.0);
      float g2 = 1.0 - clamp((Lg - uMrhi[i]) / rf, 0.0, 1.0);
      a *= g1 * g2;
    }
    if(uMcEn[i] > 0.5){
      // Hue/chroma gate — chromaticity (u,v) so it matches the ffmpeg geq with no
      // atan2. cos/sin of the target hue + cos(width) come in as uniforms.
      float uu = 2.0*col.r - col.g - col.b;
      float vv = 1.7320508*(col.g - col.b);
      float CC = sqrt(uu*uu + vv*vv);
      float chh = (uu*uMcCos[i] + vv*uMcSin[i]) / max(CC, 1e-4);
      float th = clamp((chh - uMcW[i]) / max(1.0 - uMcW[i], 1e-4), 0.0, 1.0);
      float hueW = th*th*(3.0 - 2.0*th);
      float tc = clamp((CC - (uMcChroma[i] - uMcF[i])) / max(uMcF[i], 1e-4), 0.0, 1.0);
      float chW = tc*tc*(3.0 - 2.0*tc);
      a *= hueW * chW;
    }
    vec3 lc = col * uMev[i];
    lc += uMblk[i] * (1.0 - lc);
    lc = (lc - 0.5) * uMk[i] + 0.5;
    lc.r *= 1.0 + 0.2*uMtF[i] + 0.1*uMtnF[i];
    lc.g *= 1.0 - 0.15*uMtnF[i];
    lc.b *= 1.0 - 0.2*uMtF[i] + 0.1*uMtnF[i];
    float L = dot(lc, vec3(0.2126, 0.7152, 0.0722));
    lc = L + (lc - L) * uMf[i];
    lc = clamp(lc, 0.0, 1.0);
    col = mix(col, lc, a);
  }
  frag = vec4(clamp(col, 0.0, 1.0), 1.0);
}`;

// Sum the post-LUT spatial params of two stacked grades (clip + movie). The
// shader applies one pass, so we add contributions and clamp to a sane range —
// an approximation that's plenty close for a live preview.
function combineSpatial(a, b) {
  if (!b) return a;
  const c = (x, y) => Math.max(-3, Math.min(3, (x || 0) + (y || 0)));
  return {
    clarity: c(a.clarity, b.clarity), texture: c(a.texture, b.texture), dehaze: c(a.dehaze, b.dehaze),
    denoise: Math.max(0, Math.min(1, (a.denoise || 0) + (b.denoise || 0))),
    sharpness: c(a.sharpness, b.sharpness), vignette: c(a.vignette, b.vignette), grain: c(a.grain, b.grain),
    halation: c(a.halation, b.halation), glow: c(a.glow, b.glow),
  };
}

function compileShader(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(`shader: ${log}`);
  }
  return sh;
}

// A WebGL2 canvas that renders a source (video / image / RAW float proxy)
// through the grade's 3D LUT + spatial ops. Preview matches the ffmpeg bake.
// Props: source, grade, getCrop (optional () => {x,y,w,h} normalized crop in
// source space, sampled every frame for a live subject-centered window),
// onStats(hist), onError, className, style, maxWidth.
// `baseGrade` (optional) is applied BEFORE `grade`, composited into the same 3D
// LUT — used to preview a per-clip look with the movie-wide look stacked on top
// (or vice-versa), matching exactly what the renderer bakes.
export default function GradedCanvas({ source, grade, baseGrade, getCrop, onStats, onError, className, style, maxWidth = DEFAULT_MAX_RENDER_W }) {
  const canvasRef = useRef(null);
  const glRef = useRef(null);
  const stateRef = useRef({});
  const gradeRef = useRef(grade);
  const baseGradeRef = useRef(baseGrade);
  const getCropRef = useRef(getCrop);
  const lutSigRef = useRef(null);
  const lut2SigRef = useRef(null);
  gradeRef.current = grade;
  baseGradeRef.current = baseGrade;
  getCropRef.current = getCrop;

  // One-time GL init.
  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas.getContext('webgl2', { antialias: false, preserveDrawingBuffer: true });
    if (!gl) {
      onError?.('WebGL2 is not available in this browser.');
      return undefined;
    }
    glRef.current = gl;
    gl.getExtension('OES_texture_float_linear');
    gl.getExtension('EXT_color_buffer_float');

    // Surface a lost GPU context instead of rendering a silent black frame.
    // This usually means too many live WebGL previews are alive at once (the
    // browser caps ~16 and drops the oldest); we now also release our own
    // context on unmount (below) so switching tabs / toggling the Frame strip
    // can't starve the visible studio panes.
    let unmounting = false;
    const onCtxLost = (e) => {
      e.preventDefault();
      if (!unmounting) onError?.('The preview lost its GPU context — too many live previews open. Close the Frame strip or reload the page.');
    };
    canvas.addEventListener('webglcontextlost', onCtxLost, false);

    let program;
    try {
      program = gl.createProgram();
      gl.attachShader(program, compileShader(gl, gl.VERTEX_SHADER, VERT));
      gl.attachShader(program, compileShader(gl, gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program));
      }
    } catch (err) {
      onError?.(String(err.message || err));
      return undefined;
    }
    gl.useProgram(program);

    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const imgTex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, imgTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const lutTex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_3D, lutTex);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);

    // Second 3D texture for an imported creative LUT (TEXTURE2).
    const lut2Tex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_3D, lut2Tex);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);

    stateRef.current = {
      program,
      imgTex,
      lutTex,
      lut2Tex,
      uImage: gl.getUniformLocation(program, 'uImage'),
      uLut: gl.getUniformLocation(program, 'uLut'),
      uLut2: gl.getUniformLocation(program, 'uLut2'),
      uHasLut2: gl.getUniformLocation(program, 'uHasLut2'),
      uTexel: gl.getUniformLocation(program, 'uTexel'),
      uCropOff: gl.getUniformLocation(program, 'uCropOff'),
      uCropScale: gl.getUniformLocation(program, 'uCropScale'),
      uFlip: gl.getUniformLocation(program, 'uFlip'),
      uRot: gl.getUniformLocation(program, 'uRot'),
      uImgAspect: gl.getUniformLocation(program, 'uImgAspect'),
      uDenoise: gl.getUniformLocation(program, 'uDenoise'),
      uClarity: gl.getUniformLocation(program, 'uClarity'),
      uTexture: gl.getUniformLocation(program, 'uTexture'),
      uSharp: gl.getUniformLocation(program, 'uSharp'),
      uVignette: gl.getUniformLocation(program, 'uVignette'),
      uGrain: gl.getUniformLocation(program, 'uGrain'),
      uHalation: gl.getUniformLocation(program, 'uHalation'),
      uGlow: gl.getUniformLocation(program, 'uGlow'),
      uTime: gl.getUniformLocation(program, 'uTime'),
      uLutScale: gl.getUniformLocation(program, 'uLutScale'),
      uLutOffset: gl.getUniformLocation(program, 'uLutOffset'),
      uMaskN: gl.getUniformLocation(program, 'uMaskN'),
      uMcx: gl.getUniformLocation(program, 'uMcx[0]'),
      uMcy: gl.getUniformLocation(program, 'uMcy[0]'),
      uMrx: gl.getUniformLocation(program, 'uMrx[0]'),
      uMry: gl.getUniformLocation(program, 'uMry[0]'),
      uMfeat: gl.getUniformLocation(program, 'uMfeat[0]'),
      uMinv: gl.getUniformLocation(program, 'uMinv[0]'),
      uMamt: gl.getUniformLocation(program, 'uMamt[0]'),
      uMev: gl.getUniformLocation(program, 'uMev[0]'),
      uMblk: gl.getUniformLocation(program, 'uMblk[0]'),
      uMk: gl.getUniformLocation(program, 'uMk[0]'),
      uMtF: gl.getUniformLocation(program, 'uMtF[0]'),
      uMtnF: gl.getUniformLocation(program, 'uMtnF[0]'),
      uMf: gl.getUniformLocation(program, 'uMf[0]'),
      uMtype: gl.getUniformLocation(program, 'uMtype[0]'),
      uMang: gl.getUniformLocation(program, 'uMang[0]'),
      uMrEn: gl.getUniformLocation(program, 'uMrEn[0]'),
      uMrlo: gl.getUniformLocation(program, 'uMrlo[0]'),
      uMrhi: gl.getUniformLocation(program, 'uMrhi[0]'),
      uMrf: gl.getUniformLocation(program, 'uMrf[0]'),
      uMcEn: gl.getUniformLocation(program, 'uMcEn[0]'),
      uMcCos: gl.getUniformLocation(program, 'uMcCos[0]'),
      uMcSin: gl.getUniformLocation(program, 'uMcSin[0]'),
      uMcW: gl.getUniformLocation(program, 'uMcW[0]'),
      uMcChroma: gl.getUniformLocation(program, 'uMcChroma[0]'),
      uMcF: gl.getUniformLocation(program, 'uMcF[0]'),
      uHealN: gl.getUniformLocation(program, 'uHealN'),
      uHcen: gl.getUniformLocation(program, 'uHcen[0]'),
      uHoff: gl.getUniformLocation(program, 'uHoff[0]'),
      uHr: gl.getUniformLocation(program, 'uHr[0]'),
      uHfeat: gl.getUniformLocation(program, 'uHfeat[0]'),
      healBuf: {
        cen: new Float32Array(MAX_HEAL * 2), off: new Float32Array(MAX_HEAL * 2),
        r: new Float32Array(MAX_HEAL), feat: new Float32Array(MAX_HEAL),
      },
      maskBuf: {
        cx: new Float32Array(MAX_MASKS), cy: new Float32Array(MAX_MASKS),
        rx: new Float32Array(MAX_MASKS), ry: new Float32Array(MAX_MASKS),
        feat: new Float32Array(MAX_MASKS), inv: new Float32Array(MAX_MASKS),
        amt: new Float32Array(MAX_MASKS), ev: new Float32Array(MAX_MASKS),
        blk: new Float32Array(MAX_MASKS), k: new Float32Array(MAX_MASKS),
        tF: new Float32Array(MAX_MASKS), tnF: new Float32Array(MAX_MASKS),
        f: new Float32Array(MAX_MASKS),
        type: new Float32Array(MAX_MASKS), ang: new Float32Array(MAX_MASKS),
        rEn: new Float32Array(MAX_MASKS), rlo: new Float32Array(MAX_MASKS),
        rhi: new Float32Array(MAX_MASKS), rf: new Float32Array(MAX_MASKS),
        cEn: new Float32Array(MAX_MASKS), cCos: new Float32Array(MAX_MASKS),
        cSin: new Float32Array(MAX_MASKS), cW: new Float32Array(MAX_MASKS),
        cChroma: new Float32Array(MAX_MASKS), cF: new Float32Array(MAX_MASKS),
      },
      uploadedStatic: false,
      lastStat: 0,
      statBuf: null,
    };
    gl.uniform1i(stateRef.current.uImage, 0);
    gl.uniform1i(stateRef.current.uLut, 1);
    gl.uniform1i(stateRef.current.uLut2, 2);
    gl.uniform1f(stateRef.current.uHasLut2, 0);
    gl.uniform1f(stateRef.current.uLutScale, (LUT_SIZE - 1) / LUT_SIZE);
    gl.uniform1f(stateRef.current.uLutOffset, 0.5 / LUT_SIZE);
    gl.uniform2f(stateRef.current.uCropOff, 0, 0);
    gl.uniform2f(stateRef.current.uCropScale, 1, 1);
    gl.uniform2f(stateRef.current.uFlip, 1, 1);
    gl.uniform1f(stateRef.current.uRot, 0);
    gl.uniform1f(stateRef.current.uImgAspect, 1);

    return () => {
      unmounting = true;
      canvas.removeEventListener('webglcontextlost', onCtxLost);
      gl.deleteProgram(program);
      gl.deleteTexture(imgTex);
      gl.deleteTexture(lutTex);
      gl.deleteTexture(lut2Tex);
      gl.deleteBuffer(quad);
      // Hand the GPU context back immediately so the slot is reusable, rather
      // than waiting for GC. Without this, mounting/unmounting many previews
      // (Frame strip, tab switches) eventually blanks the live panes.
      const lose = gl.getExtension('WEBGL_lose_context');
      if (lose) { try { lose.loseContext(); } catch { /* noop */ } }
      glRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-upload the 3D LUT whenever either grade's color params change. When a
  // baseGrade is present the LUT is the composite (base → grade).
  useEffect(() => {
    const gl = glRef.current;
    const st = stateRef.current;
    if (!gl || !st.lutTex) return;
    const sig = `${baseGrade ? gradeSignature(baseGrade) : ''}>>${gradeSignature(grade)}`;
    if (sig === lutSigRef.current) return;
    lutSigRef.current = sig;
    const data = baseGrade
      ? compileLutPair(baseGrade, grade, LUT_SIZE)
      : compileLut(grade, LUT_SIZE);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_3D, st.lutTex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage3D(gl.TEXTURE_3D, 0, gl.RGB32F, LUT_SIZE, LUT_SIZE, LUT_SIZE, 0, gl.RGB, gl.FLOAT, data);
  }, [grade, baseGrade]);

  // Upload the imported creative LUT (33³) into the second 3D texture, and flip
  // uHasLut2 on/off. Keyed on the LUT's id so it only re-uploads on change.
  useEffect(() => {
    const gl = glRef.current;
    const st = stateRef.current;
    if (!gl || !st.lut2Tex || st.uHasLut2 == null) return;
    const lut = grade && grade.lut && grade.lut.data ? grade.lut : null;
    if (lut && lut2SigRef.current !== lut.id) {
      const data = lutFromStored(lut.data);
      if (data && data.length === LUT_IMPORT_SIZE ** 3 * 3) {
        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_3D, st.lut2Tex);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texImage3D(gl.TEXTURE_3D, 0, gl.RGB32F, LUT_IMPORT_SIZE, LUT_IMPORT_SIZE, LUT_IMPORT_SIZE, 0, gl.RGB, gl.FLOAT, data);
        lut2SigRef.current = lut.id;
      }
    }
    gl.useProgram(st.program);
    gl.uniform1f(st.uHasLut2, lut ? 1 : 0);
  }, [grade]);

  // Render loop. Uploads the source (each frame for video; once for stills).
  useEffect(() => {
    const gl = glRef.current;
    const st = stateRef.current;
    const canvas = canvasRef.current;
    if (!gl || !st.program || !source) return undefined;

    let raf;
    let stop = false;
    st.uploadedStatic = false;
    st.uploadErr = false;

    const dims = () => {
      if (source instanceof HTMLVideoElement) return [source.videoWidth, source.videoHeight];
      if (source instanceof HTMLImageElement) return [source.naturalWidth, source.naturalHeight];
      if (source && source.width) return [source.width, source.height];
      return [0, 0];
    };

    // Normalized crop window {x,y,w,h} in source space (defaults to full frame).
    const currentCrop = () => {
      const fn = getCropRef.current;
      if (!fn) return null;
      const c = fn(source);
      if (!c || !(c.w > 0) || !(c.h > 0)) return null;
      return c;
    };

    const sizeCanvas = (crop) => {
      const [w, h] = dims();
      if (!w || !h) return false;
      // When cropping, the canvas takes the crop's pixel size so the output
      // aspect matches the exported window exactly.
      const cropW = crop ? w * crop.w : w;
      const cropH = crop ? h * crop.h : h;
      const scale = Math.min(1, maxWidth / cropW);
      const cw = Math.max(2, Math.round(cropW * scale));
      const ch = Math.max(2, Math.round(cropH * scale));
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
      }
      return true;
    };

    const uploadSource = () => {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, st.imgTex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      if (
        source instanceof HTMLVideoElement
        || source instanceof HTMLImageElement
        || source instanceof HTMLCanvasElement
        || (typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap)
      ) {
        try {
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
        } catch (e) {
          // Cross-origin taint (proxy without CORS headers) throws here — show
          // it once instead of looping on a blank frame.
          if (!st.uploadErr) {
            st.uploadErr = true;
            onError?.('Could not read this clip’s frames for preview (cross-origin). The graded look still bakes into the export.');
          }
        }
      } else if (source && source.data) {
        // RAW float proxy: Uint16Array (normalize) or Float32Array.
        const { data, width, height, channels = 3 } = source;
        const px = width * height;
        const f = new Float32Array(px * 4);
        const div = data instanceof Uint16Array ? 65535 : data instanceof Uint8Array ? 255 : 1;
        for (let p = 0; p < px; p += 1) {
          f[p * 4] = data[p * channels] / div;
          f[p * 4 + 1] = data[p * channels + 1] / div;
          f[p * 4 + 2] = data[p * channels + 2] / div;
          f[p * 4 + 3] = 1;
        }
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, width, height, 0, gl.RGBA, gl.FLOAT, f);
      }
    };

    const computeStats = () => {
      if (!onStats) return;
      const now = performance.now();
      if (now - st.lastStat < 220) return;
      st.lastStat = now;
      const w = canvas.width;
      const h = canvas.height;
      if (!st.statBuf || st.statBuf.length !== w * h * 4) st.statBuf = new Uint8Array(w * h * 4);
      gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, st.statBuf);
      const bins = 64;
      const r = new Float32Array(bins);
      const g = new Float32Array(bins);
      const b = new Float32Array(bins);
      const l = new Float32Array(bins);
      const buf = st.statBuf;
      // Waveform / parade (cols × rows) + vectorscope (VEC × VEC). Reuse buffers.
      const COLS = 256; const ROWS = 128; const VEC = 128;
      if (!st.wfR || st.wfR.length !== COLS * ROWS) {
        st.wfR = new Float32Array(COLS * ROWS);
        st.wfG = new Float32Array(COLS * ROWS);
        st.wfB = new Float32Array(COLS * ROWS);
        st.wfL = new Float32Array(COLS * ROWS);
        st.vec = new Float32Array(VEC * VEC);
      }
      const wfR = st.wfR; const wfG = st.wfG; const wfB = st.wfB; const wfL = st.wfL; const vec = st.vec;
      wfR.fill(0); wfG.fill(0); wfB.fill(0); wfL.fill(0); vec.fill(0);
      const stridePx = Math.max(1, Math.floor((w * h) / 60000));
      const stride = stridePx * 4;
      const px = w * h;
      for (let i = 0, p = 0; i < buf.length; i += stride, p += stridePx) {
        const rv = buf[i]; const gv = buf[i + 1]; const bv = buf[i + 2];
        r[(rv * bins) >> 8] += 1; g[(gv * bins) >> 8] += 1; b[(bv * bins) >> 8] += 1;
        const lum = (0.2126 * rv + 0.7152 * gv + 0.0722 * bv) | 0;
        l[(lum * bins) >> 8] += 1;
        // Waveform/parade: column = x position, row = value (top = bright).
        const x = p % w;
        const col = (x * COLS / w) | 0;
        wfR[col * ROWS + ((255 - rv) * ROWS >> 8)] += 1;
        wfG[col * ROWS + ((255 - gv) * ROWS >> 8)] += 1;
        wfB[col * ROWS + ((255 - bv) * ROWS >> 8)] += 1;
        wfL[col * ROWS + ((255 - lum) * ROWS >> 8)] += 1;
        // Vectorscope: Cb/Cr chroma plane (centered).
        const cb = -0.169 * rv - 0.331 * gv + 0.5 * bv + 128;
        const cr = 0.5 * rv - 0.419 * gv - 0.081 * bv + 128;
        const vx = (cb * VEC / 256) | 0;
        const vy = ((255 - cr) * VEC / 256) | 0;
        if (vx >= 0 && vx < VEC && vy >= 0 && vy < VEC) vec[vy * VEC + vx] += 1;
      }
      onStats({ r, g, b, l, bins, wfR, wfG, wfB, wfL, vec, cols: COLS, rows: ROWS, vec_n: VEC, samples: Math.max(1, (px / stridePx) | 0) });
    };

    const draw = () => {
      if (stop) return;
      const isVideo = source instanceof HTMLVideoElement;
      const ready = isVideo ? source.readyState >= 2 : true;
      const crop = currentCrop();
      if (ready && sizeCanvas(crop)) {
        const sp = combineSpatial(
          spatialParams(gradeRef.current),
          baseGradeRef.current ? spatialParams(baseGradeRef.current) : null
        );
        if (isVideo || !st.uploadedStatic) {
          uploadSource();
          st.uploadedStatic = true;
        }
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.uniform2f(st.uTexel, 1 / canvas.width, 1 / canvas.height);
        gl.uniform2f(st.uCropOff, crop ? crop.x : 0, crop ? crop.y : 0);
        gl.uniform2f(st.uCropScale, crop ? crop.w : 1, crop ? crop.h : 1);
        const [iw, ih] = dims();
        gl.uniform2f(st.uFlip, crop && crop.flipH ? -1 : 1, crop && crop.flipV ? -1 : 1);
        gl.uniform1f(st.uRot, crop && crop.rotate ? -(crop.rotate * Math.PI) / 180 : 0);
        gl.uniform1f(st.uImgAspect, ih ? iw / ih : 1);
        gl.uniform1f(st.uDenoise, sp.denoise || 0);
        gl.uniform1f(st.uClarity, sp.clarity);
        gl.uniform1f(st.uTexture, sp.texture);
        gl.uniform1f(st.uSharp, sp.sharpness);
        gl.uniform1f(st.uVignette, sp.vignette);
        gl.uniform1f(st.uGrain, sp.grain);
        gl.uniform1f(st.uHalation, sp.halation);
        gl.uniform1f(st.uGlow, sp.glow);
        gl.uniform1f(st.uTime, (performance.now() / 1000) % 1000);
        // Local masks: pack the active radial masks into the uniform arrays.
        const masks = activeMasks(gradeRef.current).slice(0, MAX_MASKS);
        const mb = st.maskBuf;
        masks.forEach((m, i) => {
          const s = maskScalars(m.adj);
          mb.cx[i] = m.cx; mb.cy[i] = m.cy; mb.rx[i] = m.rx; mb.ry[i] = m.ry;
          mb.feat[i] = m.feather; mb.inv[i] = m.invert ? 1 : 0; mb.amt[i] = m.amount == null ? 1 : m.amount;
          mb.ev[i] = s.ev; mb.blk[i] = s.blk; mb.k[i] = s.k; mb.tF[i] = s.tF; mb.tnF[i] = s.tnF; mb.f[i] = s.f;
          mb.type[i] = m.type === 'color' ? 2 : m.type === 'linear' ? 1 : 0;
          mb.ang[i] = ((Number(m.angle) || 0) * Math.PI) / 180;
          const rg = m.range || {};
          mb.rEn[i] = rg.enabled ? 1 : 0;
          mb.rlo[i] = Number(rg.lo) || 0;
          mb.rhi[i] = rg.hi == null ? 1 : Number(rg.hi);
          mb.rf[i] = Math.max(Number(rg.feather) || 0.001, 1e-3);
          const ck = m.colorRange || {};
          const hr = ((Number(ck.hue) || 0) * Math.PI) / 180;
          mb.cEn[i] = ck.enabled ? 1 : 0;
          mb.cCos[i] = Math.cos(hr);
          mb.cSin[i] = Math.sin(hr);
          mb.cW[i] = Math.cos(Math.max(0.035, ((Number(ck.width) || 28) * Math.PI) / 180));
          mb.cChroma[i] = Math.max(0, Number(ck.chromaMin) || 0);
          mb.cF[i] = Math.max(Number(ck.feather) || 0.05, 1e-3);
        });
        // Healing / spot removal spots (source space).
        const heal = (gradeRef.current.heal || []).filter((h) => h && Number(h.r) > 0).slice(0, MAX_HEAL);
        const hb = st.healBuf;
        heal.forEach((h, i) => {
          hb.cen[i * 2] = h.x; hb.cen[i * 2 + 1] = h.y;
          hb.off[i * 2] = h.ox || 0; hb.off[i * 2 + 1] = h.oy || 0;
          hb.r[i] = Math.max(Number(h.r) || 0.001, 1e-4);
          hb.feat[i] = h.feather == null ? 0.5 : h.feather;
        });
        gl.uniform1i(st.uHealN, heal.length);
        if (heal.length) {
          gl.uniform2fv(st.uHcen, hb.cen); gl.uniform2fv(st.uHoff, hb.off);
          gl.uniform1fv(st.uHr, hb.r); gl.uniform1fv(st.uHfeat, hb.feat);
        }
        gl.uniform1i(st.uMaskN, masks.length);
        if (masks.length) {
          gl.uniform1fv(st.uMcx, mb.cx); gl.uniform1fv(st.uMcy, mb.cy);
          gl.uniform1fv(st.uMrx, mb.rx); gl.uniform1fv(st.uMry, mb.ry);
          gl.uniform1fv(st.uMfeat, mb.feat); gl.uniform1fv(st.uMinv, mb.inv);
          gl.uniform1fv(st.uMamt, mb.amt); gl.uniform1fv(st.uMev, mb.ev);
          gl.uniform1fv(st.uMblk, mb.blk); gl.uniform1fv(st.uMk, mb.k);
          gl.uniform1fv(st.uMtF, mb.tF); gl.uniform1fv(st.uMtnF, mb.tnF); gl.uniform1fv(st.uMf, mb.f);
          gl.uniform1fv(st.uMtype, mb.type); gl.uniform1fv(st.uMang, mb.ang);
          gl.uniform1fv(st.uMrEn, mb.rEn); gl.uniform1fv(st.uMrlo, mb.rlo);
          gl.uniform1fv(st.uMrhi, mb.rhi); gl.uniform1fv(st.uMrf, mb.rf);
          gl.uniform1fv(st.uMcEn, mb.cEn); gl.uniform1fv(st.uMcCos, mb.cCos);
          gl.uniform1fv(st.uMcSin, mb.cSin); gl.uniform1fv(st.uMcW, mb.cW);
          gl.uniform1fv(st.uMcChroma, mb.cChroma); gl.uniform1fv(st.uMcF, mb.cF);
        }
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        computeStats();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      stop = true;
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  return <canvas ref={canvasRef} className={className} style={style} />;
}
